import { money, ratePerMinute, type ISODateTime, type Money, type RatePerMinute } from '../domain/branded.js';
import type { RateTableName, Resource, ResourceRatePeriod } from '../domain/resource.js';
import { parseDateTime } from './calendar-time.js';

export const RATE_TABLE_NAMES = ['A', 'B', 'C', 'D', 'E'] as const satisfies readonly RateTableName[];
export interface RateTableValidation { readonly valid: boolean; readonly errors: readonly string[]; }
export interface ResolvedResourceRate {
  readonly table: RateTableName;
  readonly fellBackToTableA: boolean;
  readonly standardRate: RatePerMinute;
  readonly overtimeRate: RatePerMinute;
  readonly costPerUse: Money;
}
const isRateTableName = (value: unknown): value is RateTableName =>
  typeof value === 'string' && (RATE_TABLE_NAMES as readonly string[]).includes(value);

/** Validate imported/edited tables without changing the resource. */
export function validateResourceRateTables(resource: Resource): RateTableValidation {
  const errors: string[] = [];
  if (!resource.rateTables) return { valid: true, errors };
  for (const [rawName, rows] of Object.entries(resource.rateTables)) {
    if (!isRateTableName(rawName)) { errors.push(`unknown rate table ${rawName}`); continue; }
    if (!Array.isArray(rows)) { errors.push(`rate table ${rawName} must be an array`); continue; }
    let previous: string | undefined;
    for (const row of rows) {
      if (!row || typeof row.effectiveFrom !== 'string' || !isLocalIsoDateTime(row.effectiveFrom)) errors.push(`rate table ${rawName} has an invalid effectiveFrom`);
      else if (previous !== undefined && row.effectiveFrom <= previous) errors.push(`rate table ${rawName} effectiveFrom values must be strictly ascending`);
      previous = row?.effectiveFrom;
      for (const [field, value] of [['standardRate', row?.standardRate], ['overtimeRate', row?.overtimeRate], ['costPerUse', row?.costPerUse]] as const) {
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) errors.push(`rate table ${rawName} has an invalid ${field}`);
      }
    }
  }
  return { valid: errors.length === 0, errors };
}

/** B-E with no rows inherit A rather than silently producing zero cost. */
export function resolveResourceRate(resource: Resource, selected: RateTableName | undefined, at: ISODateTime): ResolvedResourceRate {
  const validation = validateResourceRateTables(resource);
  if (!validation.valid) throw new Error(`Invalid resource rate tables: ${validation.errors.join('; ')}`);
  const table = selected ?? 'A';
  const selectedRows = resource.rateTables?.[table];
  const fellBackToTableA = table !== 'A' && (!selectedRows || selectedRows.length === 0);
  const row = rateAt(fellBackToTableA ? resource.rateTables?.A : selectedRows, at);
  if (row) return { table, fellBackToTableA, ...row };
  return { table, fellBackToTableA: fellBackToTableA || table === 'A', standardRate: resource.standardRate, overtimeRate: resource.overtimeRate, costPerUse: resource.costPerUse };
}
function rateAt(rows: readonly ResourceRatePeriod[] | undefined, at: ISODateTime): Pick<ResolvedResourceRate, 'standardRate' | 'overtimeRate' | 'costPerUse'> | undefined {
  let selected: ResourceRatePeriod | undefined;
  for (const row of rows ?? []) { if (row.effectiveFrom > at) break; selected = row; }
  return selected;
}
function isLocalIsoDateTime(value: string): boolean {
  try { parseDateTime(value as ISODateTime); return true; } catch { return false; }
}
export function resourceRatePeriod(effectiveFrom: ISODateTime, standardRate: number, overtimeRate: number, costPerUse: number): ResourceRatePeriod {
  return { effectiveFrom, standardRate: ratePerMinute(standardRate), overtimeRate: ratePerMinute(overtimeRate), costPerUse: money(costPerUse) };
}
